import { curry, path, pluck, reverse } from 'ramda'
import { isIosApp, isAndroidApp, isApp, appIsGteVersion } from '../../common/ambient'

/**
 * Checks if the app version meets requirements for subscription and redirects to store if needed
 * @param {string} subscription - The subscription product name (e.g., 'awn-plus-for-teams-yearly')
 * @returns {boolean} - true if redirect occurred (version too old), false if version is OK
 */
export const doAppVersionRedirect = (subscription) => {
  // Check version requirements
  if (!appIsGteVersion('4.5.0') ||
      (!appIsGteVersion('4.6.0') && /for-teams/.test(subscription))) {

    // Determine store URL based on platform
    const storeUrl = isIosApp()
      ? 'https://apps.apple.com/us/app/ambient-weather-network/id1426025887'
      : 'https://play.google.com/store/apps/details?id=net.ambientweather.dashboard'

    // Alert user to update
    window.alert('Please update your app to the latest version to subscribe.')

    // Open store
    window.open(storeUrl, '_blank')

    return true
  }

  return false
}

// Warning; array MUST be ordered from lowest tier to highest tier
export const CAM_TIERS = [{
  label: 'Standard',
  displayName: 'Camera Standard',
  price: 0,
  period: 'month',
  displayPrice: 'Free',
  timelapseRes: '640x320',
  regenRate: '4 times per day',
  banner: 'Standard',
  sampling: '5 minutes',
  storage: '1 day',
  mapIcon: 'Standard',
  productName: '',
  term: 'both'
}, {
  label: 'Silver Monthly',
  displayName: 'Camera Silver Monthly',
  price: 4.99,
  period: 'month',
  displayPrice: '$4.99/month',
  timelapseRes: '1280x720',
  regenRate: '6 times per day',
  banner: 'HD',
  sampling: '3 minutes',
  storage: '30 days',
  mapIcon: 'Silver',
  productName: 'awn-camera-tier1-monthly',
  term: 'monthly'
}, {
  label: 'Silver Annual',
  displayName: 'Camera Silver Annual',
  price: 49.99,
  period: 'year',
  displayPrice: '$49.99/year',
  timelapseRes: '1280x720',
  regenRate: '6 times per day',
  banner: 'HD',
  sampling: '3 minutes',
  storage: '30 days',
  mapIcon: 'Silver',
  productName: 'awn-camera-tier1-yearly',
  term: 'yearly'
}, {
  label: 'Gold Monthly',
  displayName: 'Camera Gold Monthly',
  price: 9.99,
  period: 'month',
  displayPrice: '$9.99/month',
  timelapseRes: '1920x1080',
  regenRate: '8 times per day',
  banner: 'Full HD',
  sampling: '3 minutes',
  storage: '60 days',
  mapIcon: 'Gold',
  productName: 'awn-camera-tier2-monthly',
  term: 'monthly'
}, {
  label: 'Gold Annual',
  displayName: 'Camera Gold Annual',
  price: 99.99,
  period: 'year',
  displayPrice: '$99.99/year',
  timelapseRes: '1920x1080',
  regenRate: '8 times per day',
  banner: 'Full HD',
  sampling: '3 minutes',
  storage: '60 days',
  mapIcon: 'Gold',
  productName: 'awn-camera-tier2-yearly',
  term: 'yearly'
}]

export const CAM_FEATURES = [
  {
    label: 'Time-lapse resolution',
    standard: { value: '640 x 320', sub: 'Standard', res: '320p' },
    silver: { value: '1280 x 720', sub: 'HD', res: '720p' },
    gold: { value: '1920 x 1080', sub: 'Full HD', res: '1080p' }
  },
  {
    label: 'Regeneration rate',
    standard: '4 times/day',
    silver: '6 times/day',
    gold: '8 times/day'
  },
  {
    label: 'Video sampling',
    standard: '5 min',
    silver: '3 min',
    gold: '3 min'
  },
  {
    label: 'Time-lapse storage',
    standard: '1 day',
    silver: '30 days',
    gold: '60 days'
  },
  {
    label: 'Video library',
    description: 'Review and manage all of your stored videos.',
    standard: false,
    silver: true,
    gold: true
  },
  {
    label: 'Weather cam dashboard',
    description: 'In-depth review of your videos with detailed weather charts and data graphing.',
    standard: false,
    silver: true,
    gold: true
  }
]

const camProductNames = pluck('productName', CAM_TIERS)

export const CELL_SUBSCRIPTIONS = [{
  productName: 'kestrelmet-one-year-subscription-new',
  label: 'KestrelMet Standard 1 Year Cellular Plan',
  displayName: 'KestrelMet Standard 1 Year Cellular Plan',
  price: 99.99,
  period: 'year',
  displayPrice: '$99.99',
  SKU: 281055
}, {
  productName: 'kestrelmet-one-year-subscription',
  label: 'KestrelMet Standard 1 Year Cellular Plan',
  displayName: 'KestrelMet Standard 1 Year Cellular Plan',
  price: 49.99,
  period: 'year',
  displayPrice: '$49.99',
  SKU: 281055
}, {
  productName: 'kestrelmet-three-year-subscription',
  label: 'KestrelMet Standard 3 Year Cellular Plan',
  displayName: 'KestrelMet Standard 3 Year Cellular Plan',
  price: 150,
  period: 'year',
  displayPrice: '$150.00',
  SKU: 281057
}, {
  productName: 'kestrelmet-pro-one-year-subscription',
  label: 'KestrelMet Pro 1 Year Cellular Plan',
  displayName: 'KestrelMet Pro 1 Year Cellular Plan',
  price: 120,
  period: 'year',
  displayPrice: '$120.00',
  SKU: 281053
}, {
  productName: 'kestrelmet-pro-three-year-subscription',
  label: 'KestrelMet Pro 3 Year Cellular Plan',
  displayName: 'KestrelMet Pro 3 Year Cellular Plan',
  price: 360,
  period: 'year',
  displayPrice: '$360.00',
  SKU: 281058
}]

export const HEAT_STRESS_SUBSCRIPTIONS = [{
  productName: 'heat-stress-standard-monthly',
  displayName: 'Heat Stress Standard',
  price: 19.99,
  period: 'month',
  displayPrice: '$19.99/month',
  monthlySms: 1000,
}, {
  productName: 'heat-stress-pro-monthly',
  displayName: 'Heat Stress Pro',
  price: 29.99,
  period: 'month',
  displayPrice: '$29.99/month',
  monthlySms: 2000,
}, {
  productName: 'heat-stress-enterprise-monthly',
  displayName: 'Heat Stress Enterprise',
  price: 39.99,
  period: 'month',
  displayPrice: '$39.99/month',
  monthlySms: 3000,
}]

export const HEAT_STRESS_FEATURES = [
  {
    label: 'Email alerts',
    description: 'Receive timely heat stress warnings directly in your inbox.',
    free: true,
    standard: true,
    pro: true,
    enterprise: true
  },
  {
    label: 'SMS alerts',
    description: 'Stay informed anywhere, anytime - even without internet connection, with text message alerts.',
    free: false,
    standard: '1000 SMS /month',
    pro: '2000 SMS /month',
    enterprise: '3000 SMS /month'
  },
  {
    label: 'Notified users',
    description: 'Add multiple family or team members to receive critical alerts.',
    free: false,
    standard: 'up to 5 people',
    pro: 'up to 10 people',
    enterprise: 'up to 15 people'
  },
  {
    label: 'Automatic SMS refill *',
    description: 'Ensuring safety and deliver critical alerts with auto top-ups',
    free: false,
    standard: true,
    pro: true,
    enterprise: true
  }
]

// Heat Stress tier configuration with member limits
// Note: maxMembers includes the admin, so "up to 5 people" = 6 maxMembers (admin + 5 additional)
export const HEAT_STRESS_TIER_LIMITS = {
  free: { maxMembers: 1, smsEnabled: false, displayName: 'Heat Stress Free' },
  standard: { maxMembers: 6, smsEnabled: true, displayName: 'Heat Stress Standard' },
  pro: { maxMembers: 11, smsEnabled: true, displayName: 'Heat Stress Pro' },
  enterprise: { maxMembers: 16, smsEnabled: true, displayName: 'Heat Stress Enterprise' }
}

/**
 * Gets the current Heat Stress subscription tier from user subscriptions
 * @param {object} user - User object (can be user state or user.info)
 * @returns {string} - Tier name ('free', 'standard', 'pro', or 'enterprise')
 */
export const getHeatStressTier = (user) => {
  const theUser = path(['info'], user) || user
  if (!theUser) return 'free'

  const subscriptions = theUser?.payments?.subscriptions || []
  const heatStressSub = subscriptions.find(sub =>
    sub.productName?.startsWith('heat-stress-') &&
    (!sub.macAddress || sub.macAddress === 'Account')
  )

  if (!heatStressSub) return 'free'
  if (heatStressSub.productName.includes('standard')) return 'standard'
  if (heatStressSub.productName.includes('pro')) return 'pro'
  if (heatStressSub.productName.includes('enterprise')) return 'enterprise'
  return 'free'
}

/**
 * Gets tier limits for a Heat Stress subscription tier
 * @param {string} tier - Tier name
 * @returns {object} - { maxMembers, smsEnabled, displayName }
 */
export const getHeatStressTierLimits = (tier) => {
  return HEAT_STRESS_TIER_LIMITS[tier] || HEAT_STRESS_TIER_LIMITS.free
}

export const USER_SUBSCRIPTIONS = [{
  productName: 'awn-plus-account-monthly',
  displayName: 'AWN+ Individual',
  price: 4.99,
  period: 'month',
  displayPrice: '$4.99/month',
  appSubscriptionId: 281024,
  forecastDays: 10
}, {
  productName: 'awn-plus-account-yearly',
  displayName: 'AWN+ Individual',
  price: 49.99,
  period: 'year',
  displayPrice: '$49.99/year',
  appSubscriptionId: 281025,
  forecastDays: 10
}, {
  productName: 'awn-plus-for-teams-monthly',
  displayName: 'AWN+ Teams',
  price: 9.99,
  period: 'month',
  displayPrice: '$9.99/month',
  appSubscriptionId: 281026,
  forecastDays: 15
}, {
  productName: 'awn-plus-for-teams-yearly',
  displayName: 'AWN+ Teams',
  price: 99.99,
  period: 'year',
  displayPrice: '$99.99/year',
  appSubscriptionId: 281027,
  forecastDays: 15
}, {
  productName: 'awn-plus-for-teams-free',
  displayName: 'AWN+ Teams (Free)',
  price: 0,
  period: 'year',
  displayPrice: 'Free',
  forecastDays: 15
}, {
  productName: 'awn_sms_notifications',
  displayName: 'SMS Notifications',
  price: 1.50,
  period: 'one-time',
  displayPrice: '$1.50/100 SMS'
}]

export const displayPriceForSubscription = (sub) => {
  if (!sub) {
    return 'Unknown Subscription'
  }
  if (subIsPlus(sub) && sub.promoCode && !sub.autoRenewChangeIsAllowed) {
    return 'Free'
  }
  if (sub.price) {
    return sub.currency + sub.price
  }
  if (sub.pricingInfo) {
    return sub.pricingInfo.currency + sub.pricingInfo.amount
  }
  const config = getSubscriptionConfig(sub.productName)
  return config.displayPrice
}
export const isCellularDevice = (device) => path(['lastData', 'stationtype'], device) === 'NK_KestrelMet6000' || path(['settings', 'external', 'rw', 'stationType'], device) === 'KestrelMet6000'
export const isCellularSubscription = (productName) => !!CELL_SUBSCRIPTIONS.find(entry => entry.productName === productName)
export const hasCellularSubscription = curry((user, macAddress) => {
  return (
    (path(['info', 'payments', 'subscriptions'], user) || [])
      .filter(sub => sub.macAddress === macAddress)
      .filter(sub => isCellularSubscription(sub.productName))
      .length > 0
  )
})
export const hasCellularDevice = (devices) => {
  return (
    (devices || [])
      .filter(device => isCellularDevice(device))
      .length > 0
  )
}
export const isKestrelMet = (device) =>
  path(['lastData', 'stationtype'], device) === 'NK_KestrelMet6000' ||
  path(['lastData', 'stationtype'], device) === 'NK_KestrelMet6000W'

export const hasRainWiseStatus = isKestrelMet

export const userHasPayments = path(['info', 'payments'])

export const userHasCardOnFile = path(['info', 'payments', 'defaultPaymentMethod'])
export const deviceHasSubscription = curry((user, macAddress) => {
  return (path(['info', 'payments', 'subscriptions'], user) || []).find(sub => sub.macAddress === macAddress)
})

export const deviceHasThisSub = curry((user, macAddress, productName) => {
  return (
    (path(['info', 'payments', 'subscriptions'], user) || [])
      .filter(sub => sub.macAddress == macAddress)
      .find(sub => sub.productName === productName)
  )
})

export const deviceCamTierI = curry((user, macAddress) => {
  const lastTierIndex = CAM_TIERS.length - 1
  const i = reverse(camProductNames).findIndex((prodName) => deviceHasThisSub(user, macAddress, prodName))
  return i > -1 ? lastTierIndex - i : 0
})

export const deviceCamTierObj = curry((user, macAddress) => {
  const i = deviceCamTierI(user, macAddress)
  const ret = Object.assign({ tier: i }, CAM_TIERS[i])
  return ret
})

/**
 * Maps camera tier index to simple tier name
 * @param {number} tierIndex - Tier index (0-4)
 * @returns {string} - Tier name ('standard', 'silver', or 'gold')
 */
export const getCameraTierName = (tierIndex) => {
  if (tierIndex === 0) return 'standard'
  if (tierIndex === 1 || tierIndex === 2) return 'silver' // Monthly or Annual
  if (tierIndex === 3 || tierIndex === 4) return 'gold'   // Monthly or Annual
  return 'standard'
}

/**
 * Detects if a product name is a camera subscription
 * @param {string} productName - The product name to check
 * @returns {boolean} - True if camera subscription
 */
export const isCameraSubscription = (productName) => {
  if (!productName) return false
  return /^awn-camera-/.test(productName)
}

/**
 * Detects if a product name is a Heat Stress subscription
 * @param {string} productName - The product name to check
 * @returns {boolean} - True if Heat Stress subscription
 */
export const isHeatStressSubscription = (productName) => {
  if (!productName) return false
  return /^heat-stress-/.test(productName)
}

/**
 * Gets the display price for any subscription type (AWN+ or camera)
 * Returns price without the period suffix (e.g., "$49.99" not "$49.99/year")
 * @param {string} productName - The product name
 * @returns {string} - Display price (e.g., "$49.99")
 */
export const getSubscriptionDisplayPrice = (productName) => {
  const config = getSubscriptionConfig(productName)
  if (!config) return '$0.00'

  // Use standardized displayPrice (should always be available now)
  if (config.displayPrice) {
    return config.displayPrice
  }

  // Final fallback: format the numeric price
  if (typeof config.price === 'number') {
    return `$${config.price.toFixed(2)}`
  }

  return '$0.00'
}

// Stripe Subscription Object
// status enum
// Possible values are incomplete, incomplete_expired, trialing, active, past_due, canceled, or unpaid.
// also "free" which is created internally
export const deviceSubscriptionStatus = curry((user, macAddress, productName) => {
  const sub = (path(['info', 'payments', 'subscriptions'], user) || [])
    .filter(sub => sub.macAddress === macAddress)
    .find(sub => sub.productName === productName)
  return (sub ? sub.status : '')
})

/**
 * Gets CSS class names for subscription card styling based on subscription type
 * @param {object} sub - Subscription object or object with productName property
 * @returns {object} - Object with CSS class names as keys and true as values
 */
export const getSubscriptionTypeClasses = (sub) => {
  if (!sub || !sub.productName) return {}

  const productName = sub.productName

  // AWN+ Individual subscriptions
  if (/^awn-plus-account-/.test(productName)) {
    return {
      'plus': true
    }
  }

  // AWN+ Teams subscriptions
  if (/^awn-plus-for-teams-/.test(productName)) {
    return {
      'plus-teams': true
    }
  }

  // Camera subscriptions
  if (isCameraSubscription(productName)) {
    const classes = {
      'camera': true
    }
    // Add tier-specific classes
    if (/tier1/.test(productName)) {
      classes['camera-silver'] = true
    } else if (/tier2/.test(productName)) {
      classes['camera-gold'] = true
    }
    return classes
  }

  // KestrelMet Cellular subscriptions
  if (/^kestrelmet-/.test(productName)) {
    return {
      'cellular': true
    }
  }

  // SMS Notifications
  if (productName === 'awn_sms_notifications') {
    return {
      'sms': true
    }
  }

  // Heat Stress subscriptions
  if (isHeatStressSubscription(productName)) {
    const classes = {
      'heat-stress': true
    }
    // Add tier-specific classes
    if (/standard/.test(productName)) {
      classes['heat-stress-standard'] = true
    } else if (/pro/.test(productName)) {
      classes['heat-stress-pro'] = true
    } else if (/enterprise/.test(productName)) {
      classes['heat-stress-enterprise'] = true
    }
    return classes
  }

  return {}
}

/**
 * Gets comprehensive subscription details for display in success messages
 * Works for AWN+ subscriptions, camera subscriptions, cellular subscriptions, and Heat Stress subscriptions
 * @param {object} user - User object
 * @param {string} subscription - Product name (e.g., 'awn-plus-account-yearly', 'awn-camera-silver-yearly', 'heat-stress-pro-monthly')
 * @param {object} currentDevice - Device object (required for camera/cellular subscriptions, null for AWN+/Heat Stress)
 * @returns {object} - { displayName, renewalDate, displayPrice, deviceInfo, status, isProcessing }
 */
export const getSubscriptionDetails = (user, subscription, currentDevice = null) => {
  const isCameraSub = isCameraSubscription(subscription)
  const isCellSub = isCellularSubscription(subscription)
  const isHeatStressSub = isHeatStressSubscription(subscription)
  const config = getSubscriptionConfig(subscription)

  if (isCameraSub) {
    // Camera subscription details
    const macAddress = currentDevice?.macAddress
    if (!macAddress) {
      return {
        displayName: config?.label || config?.displayName || 'Camera Subscription',
        renewalDate: null,
        displayPrice: config?.displayPrice || '$0.00',
        deviceInfo: null,
        status: null,
        isProcessing: true
      }
    }

    const allDeviceSubs = (path(['info', 'payments', 'subscriptions'], user) || [])
      .filter(sub => sub.macAddress === macAddress)

    // Try to find exact match first (for upgrades/new subscriptions)
    let deviceSub = allDeviceSubs.find(sub => sub.productName === subscription)
    let isScheduledChange = false

    // If not found, check for scheduled downgrade to this subscription
    if (!deviceSub) {
      deviceSub = allDeviceSubs.find(sub =>
        sub.willDowngradeTo === subscription &&
        (sub.status === 'active' || sub.status === 'trialing')
      )
      if (deviceSub) {
        isScheduledChange = true
      }
    }

    const deviceName = currentDevice?.name || currentDevice?.info?.name || 'Unknown Device'

    return {
      displayName: config?.label || config?.displayName || 'Camera Subscription',
      renewalDate: deviceSub?.currentPeriodEnd ? new Date(deviceSub.currentPeriodEnd * 1000) : null,
      displayPrice: config?.displayPrice || '$0.00',
      deviceInfo: {
        name: deviceName,
        macAddress: macAddress
      },
      status: deviceSub?.status || null,
      isScheduledChange
    }
  } else if (isCellSub) {
    // Cellular subscription details
    const macAddress = currentDevice?.macAddress
    if (!macAddress) {
      return {
        displayName: config?.label || 'KestrelMet Cellular Subscription',
        renewalDate: null,
        displayPrice: config?.displayPrice || '$0.00',
        deviceInfo: null,
        status: null,
        isProcessing: true
      }
    }

    const deviceSub = (path(['info', 'payments', 'subscriptions'], user) || [])
      .filter(sub => sub.macAddress === macAddress)
      .find(sub => sub.productName === subscription)

    const deviceName = currentDevice?.name || currentDevice?.info?.name || 'Unknown Device'

    return {
      displayName: config?.label || 'KestrelMet Cellular Subscription',
      renewalDate: deviceSub?.currentPeriodEnd ? new Date(deviceSub.currentPeriodEnd * 1000) : null,
      displayPrice: config?.displayPrice || '$0.00',
      deviceInfo: {
        name: deviceName,
        macAddress: macAddress
      },
      status: deviceSub?.status || null,
      isProcessing: !deviceSub || deviceSub.status === 'incomplete'
    }
  } else if (isHeatStressSub) {
    // Heat Stress subscription details (account-level)
    const allHeatStressSubs = (path(['info', 'payments', 'subscriptions'], user) || [])
      .filter(sub => !sub.macAddress || sub.macAddress === 'Account')

    // Try to find exact match first
    let heatStressSub = allHeatStressSubs.find(sub => sub.productName === subscription)
    let isScheduledChange = false

    // If not found, check for scheduled downgrade to this subscription
    if (!heatStressSub) {
      heatStressSub = allHeatStressSubs.find(sub =>
        sub.willDowngradeTo === subscription &&
        (sub.status === 'active' || sub.status === 'trialing')
      )
      if (heatStressSub) {
        isScheduledChange = true
      }
    }

    return {
      displayName: config?.displayName || 'Heat Stress Subscription',
      renewalDate: heatStressSub?.currentPeriodEnd ? new Date(heatStressSub.currentPeriodEnd * 1000) : null,
      displayPrice: config?.displayPrice || '$0.00',
      deviceInfo: null,
      status: heatStressSub?.status || null,
      isScheduledChange
    }
  } else {
    // AWN+ subscription details
    let plusSub = user.plus
    let isScheduledChange = false

    // If the active subscription doesn't match, check for scheduled downgrade
    if (plusSub && plusSub.productName !== subscription) {
      // Check if active subscription is scheduled to downgrade to target
      if (plusSub.willDowngradeTo === subscription &&
          (plusSub.status === 'active' || plusSub.status === 'trialing')) {
        isScheduledChange = true
      } else {
        // Not found and no scheduled downgrade
        plusSub = null
      }
    }

    return {
      displayName: plusSub ? getSubscriptionDisplayName(plusSub) : (config?.displayName || 'AWN+ Subscription'),
      renewalDate: plusSub?.currentPeriodEnd ? new Date(plusSub.currentPeriodEnd * 1000) : null,
      displayPrice: config?.displayPrice || '$0.00',
      deviceInfo: null,
      status: plusSub?.status || null,
      isScheduledChange
    }
  }
}

const activeSubscriptionStatuses = ['active', 'trialing', 'past_due', 'free']
export const subIsActive = sub => {
  if (!sub) {
    return false
  }
  if (typeof sub.provisionAccess !== 'undefined') {
    return sub.provisionAccess
  }
  return activeSubscriptionStatuses.includes(sub.status)
}

// this can take the user state OR the user object (contained in user.info)
export const isPlus = user => {
  const theUser = path(['info'], user) || user
  if (!theUser) return false
  const plusSub = getPlusSub(user)
  if (plusSub && subIsActive(plusSub)) {
    return plusSub
  }
  if (theUser.roles && theUser.roles.includes('awn-plus')) {
    return {
      productName: 'awn-plus-free'
    }
  } else if (theUser.roles && theUser.roles.includes('awn-plus-teams')) {
    return {
      productName: 'awn-plus-for-teams-free'
    }
  }
  return false
}

export const displayCancelPlusOnIosMessage = user => {
  const allPlusSubs = getAllPlusSubs(user)
  if (allPlusSubs.length > 1) {
    const iosSubs = allPlusSubs.filter(subIsIos)
    if (iosSubs[0]) {
      if (!iosSubs[0].cancelAtPeriodEnd) {
        return true
      }
    }
  }
}

export const getAllPlusSubs = user => {
  const theUser = path(['info'], user) || user
  if (!theUser) return false
  const subscriptions = path(['payments', 'subscriptions'], theUser) || []
  return subscriptions.filter(subIsPlus)
}

export const getPlusSub = user => {
  const theUser = path(['info'], user) || user
  if (!theUser) return false
  const subs = getAllPlusSubs(user)
  if (!subs.length) {
    return false
  } else if (subs.length === 1) {
    return subs[0]
  }
  return subs.find(s => s.promoCode) // favor the one with a promo code
}
export const plusIsTrial = user => {
  return getPlusSub(user) && getPlusSub(user).status === 'trialing'
}
export const plusSubHasCode = user => {
  return getPlusSub(user) && getPlusSub(user).promoCode
}

export const subIsPlus = sub => {
  if (!sub || !sub.productName) {
    return false
  }
  return /^awn-plus-/.test(sub.productName)
}
export const subIsTeams = sub => {
  if (!sub || !sub.productName) {
    return false
  }
  return /^awn-plus-for-teams-/.test(sub.productName)
}
export const subIsAndroid = sub => {
  if (!sub) {
    return false
  }
  return sub.googleProductId
}
export const subIsIos = sub => {
  if (!sub) {
    return false
  }
  return sub.appleProductId
}
export const subIsIap = sub => {
  if (!sub) {
    return false
  }
  return subIsAndroid(sub) || subIsIos(sub)
}

/**
 * Detects if an IAP subscription's original platform doesn't match current device
 */
export const hasIapPlatformMismatch = (sub) => {
  if (!subIsIap(sub)) {
    return false
  }

  // iOS subscription but not on iOS device
  if (subIsIos(sub) && !isIosApp()) {
    return true
  }

  // Android subscription but not on Android device
  if (subIsAndroid(sub) && !isAndroidApp()) {
    return true
  }

  return false
}

/**
 * Gets the appropriate store management URL for an IAP subscription
 */
export const getIapManagementUrl = (sub) => {
  if (!subIsIap(sub)) {
    return null
  }

  if (subIsIos(sub)) {
    return 'https://apps.apple.com/account/subscriptions'
  } else if (subIsAndroid(sub)) {
    return `https://play.google.com/store/account/subscriptions?sku=${sub.googleProductId}&package=net.ambientweather.dashboard`
  }

  return null
}

/**
 * Gets the store name for display
 */
export const getIapStoreName = (sub) => {
  return subIsIos(sub) ? 'Apple App Store' : 'Google Play Store'
}

export const subIsIosDowngradedAnnual = sub => {
  if (!sub || !sub.productName) {
    return false
  }
  return subIsIos(sub) && /yearly/.test(sub.productName) && sub.willDowngradeAtPeriodEnd
}
export const subIsMonthly = sub => {
  if (!sub || !sub.productName) {
    return false
  }
  if (/monthly/.test(sub.productName)) {
    return true
  }
  if (subIsIosDowngradedAnnual(sub)) {
    return true
  }
  return false
}

export const subStatusDisplay = sub => {
  if (!sub || !sub.status) {
    return ''
  }
  if (sub.promoCode) {
    return 'Gift subscription'
  }
  if (sub.status === 'trialing') {
    if (sub?.trialType === 'proration') return 'Active'
    return 'Free Trial'
  }
  return sub.status.charAt(0).toUpperCase() + sub.status.slice(1).replace('_', ' ')
}

export const getExpiredCellularSubscription = curry((user, macAddress) => {
  const transactions = path(['info', 'payments', 'transactions'], user) || []
  const cellularTransactions = transactions
    .filter(transaction =>
      transaction.macAddress === macAddress &&
      isCellularSubscription(transaction.productName)
    )
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

  if (cellularTransactions.length > 0 && cellularTransactions[0].type === 'customer.subscription.deleted') {
    return cellularTransactions[0]
  }
  return null
})
// Helper to get subscription config by product name
// Searches USER_SUBSCRIPTIONS (AWN+), CAM_TIERS (cameras), CELL_SUBSCRIPTIONS (cellular), and HEAT_STRESS_SUBSCRIPTIONS
export const getSubscriptionConfig = (productName) => {
  // Try USER_SUBSCRIPTIONS first (AWN+, Teams, SMS, etc.)
  const userSub = USER_SUBSCRIPTIONS.find(sub => sub.productName === productName)
  if (userSub) return userSub

  // Try CAM_TIERS (camera subscriptions)
  const camTier = CAM_TIERS.find(tier => tier.productName === productName)
  if (camTier) return camTier

  // Try CELL_SUBSCRIPTIONS (cellular/KestrelMet subscriptions)
  const cellSub = CELL_SUBSCRIPTIONS.find(sub => sub.productName === productName)
  if (cellSub) return cellSub

  // Try HEAT_STRESS_SUBSCRIPTIONS
  const heatStressSub = HEAT_STRESS_SUBSCRIPTIONS.find(sub => sub.productName === productName)
  if (heatStressSub) return heatStressSub

  return null
}

// Helper to get app subscription ID from product name
export const getAppSubscriptionId = (productName) => {
  const config = getSubscriptionConfig(productName)
  return config ? config.appSubscriptionId : null
}

// Trigger native IAP flow for iOS/Android apps
export const triggerNativeIAP = (productName, userId) => {
  const subscriptionConfig = getSubscriptionConfig(productName)
  const appSubscriptionId = subscriptionConfig && subscriptionConfig.appSubscriptionId

  if (!appSubscriptionId || !userId) {
    console.error('triggerNativeIAP: Missing appSubscriptionId or userId', { productName, userId, appSubscriptionId })
    return false
  }

  try {
    if (typeof window !== 'undefined') {
      // iOS IAP flow
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.buySubscriptionWebNotification) {
        window.webkit.messageHandlers.buySubscriptionWebNotification.postMessage({
          userId,
          subscriptionId: appSubscriptionId
        })
        return true
      }

      // Android IAP flow
      if (window.ANDROID && typeof window.ANDROID.buySubscriptionWebNotification === 'function') {
        window.ANDROID.buySubscriptionWebNotification(appSubscriptionId, userId)
        return true
      }
    }

    console.error('triggerNativeIAP: Native IAP interface not available')
    return false
  } catch (error) {
    console.error('triggerNativeIAP: Error triggering IAP', error)
    return false
  }
}

// Helper to get subscription display name
export const getSubscriptionDisplayName = (subscription) => {
  if (!subscription || !subscription.productName) {
    return 'AWN+'
  }

  const config = getSubscriptionConfig(subscription.productName)
  let displayName

  // Camera subscriptions use the label field
  if (isCameraSubscription(subscription.productName)) {
    displayName = config ? config.label : subscription.nickname
  } else if (isCellularSubscription(subscription.productName)) {
    // Cellular subscriptions use the label field
    displayName = config ? config.label : subscription.nickname
  } else {
    // AWN+ subscriptions use the displayName field
    displayName = config ? config.displayName : subscription.nickname
  }

  // if (subscription.promoCode) {
  //   displayName += ` (Gift Code: ${subscription.promoCode})`
  // }

  return displayName
}

// only returns something is the app has configured the product details
export const getAwnPlusAppPrice = (device, annual) => {
  const productName = annual ? 'awn-plus-account-yearly' : 'awn-plus-account-monthly'
  const appSubscriptionId = getAppSubscriptionId(productName)
  if (isApp() && appSubscriptionId) {
    if (device.appProductDetails && Array.isArray(device.appProductDetails)) {
      const p = device.appProductDetails.find(item => parseInt(item.id, 10) === appSubscriptionId)
      if (p && p.displayPrice) {
        return p.displayPrice.replace(/USD\s+/, '$')
      }
    }
    return '- -'
  }
}

// only returns something is the app has configured the product details
export const getAwnTeamsAppPrice = (device, annual) => {
  const productName = annual ? 'awn-plus-for-teams-yearly' : 'awn-plus-for-teams-monthly'
  const appSubscriptionId = getAppSubscriptionId(productName)
  if (isApp() && appSubscriptionId) {
    if (device.appProductDetails && Array.isArray(device.appProductDetails)) {
      const p = device.appProductDetails.find(item => parseInt(item.id, 10) === appSubscriptionId)
      if (p && p.displayPrice) {
        return p.displayPrice.replace(/USD\s+/, '$')
      }
    }
    return '- -'
  }
}

export const freeTrialUsed = (user, subKey) => {
  return path(['info', 'payments', 'freeTrialsUsed', subKey], user)
}

export const showAwnFreeTrial = user => {
  const freeTrialsUsed = path(['info', 'payments', 'freeTrialsUsed'], user) || {}
  // Check if any key starts with 'awn-plus'
  const hasUsedAwnPlusTrial = Object.keys(freeTrialsUsed).some(key => key.startsWith('awn-plus'))
  return !hasUsedAwnPlusTrial
}

export const awnPlusAnnualMonthlySwitchAvail = (user, sub) => {
  // If sub is provided, use it for specific subscription checks
  if (sub) {
    // Check if it's an AWN Plus subscription
    if (!/awn-plus/.test(sub.productName)) return false

    // Check user-level conditions (user.plus exists and is not IAP)
    if (!awnPlusAnnualMonthlySwitchAvail(user)) return false

    // Check if it's an iOS downgraded annual
    if (subIsIosDowngradedAnnual(sub)) return false

    // Check if it's a promo code with plan change not allowed
    if (sub.promoCode && !sub.planChangeIsAllowed) return false

    return true
  }

  // Legacy behavior when called with just user (for backward compatibility)
  const userSub = user.plus
  if (!userSub) return false
    if (subIsIap(userSub)) {
    if (subIsIos(userSub) && !isIosApp()) {
      return null  // Apple subscription but not on iOS device
    }
    if (!subIsIos(userSub) && !isAndroidApp()) {
      return null  // Google subscription but not on Android device
    }
  }
  return true
}

export const getHourlyForecastLimit = user => {
  return user.plus ? 240 : 72 // Plus members get 10 days (240 hours), regular users get 3 days (72 hours)
}

export const getDailyForecastLimit = user => {
  if (!user.plus) return 10 // Regular users get 10 days

  const plusSub = getPlusSub(user)
  if (plusSub && plusSub.productName) {
    const config = getSubscriptionConfig(plusSub.productName)
    if (config && config.forecastDays) {
      return config.forecastDays
    }
  }

  return 10 // Default for Plus members
}

// Helper to determine if a subscription is account-level (not tied to a specific device)
export const isAccountLevelSubscription = sub => {
  if (!sub) return false
  return !sub.macAddress || sub.macAddress === 'Account'
}

// Helper to determine if a subscription is device-level (tied to a specific device)
export const isDeviceLevelSubscription = sub => {
  if (!sub) return false
  return sub.macAddress && sub.macAddress !== 'Account'
}

// Generic curried function for subscription pricing
export const createSubscriptionPriceGetter = curry((device, tier, isAnnual, useAppPrice) => {
  const isTeams = tier === 'teams'
  const productName = `awn-plus-${isTeams ? 'for-teams' : 'account'}-${isAnnual ? 'yearly' : 'monthly'}`
  const config = getSubscriptionConfig(productName)

  // Only use app price when explicitly requested AND in app context
  if (useAppPrice && isApp()) {
    const appPrice = isTeams ? getAwnTeamsAppPrice(device, isAnnual) : getAwnPlusAppPrice(device, isAnnual)
    if (appPrice && appPrice !== '- -') {
      const priceNum = parseFloat(appPrice.replace(/[^0-9.]/g, ''))
      return {
        price: appPrice,
        monthlyPrice: isAnnual ? `$${(priceNum / 12).toFixed(2)}` : appPrice.replace('/month', ''),
        billedText: isAnnual ? `Billed annually ${appPrice}/year` : null,
        rawPrice: priceNum
      }
    }
  }

  // Get price from config - use displayPrice (string) if available, fallback to price (number)
  let stripePrice
  let priceNum

  if (config) {
    if (config.displayPrice) {
      // New standardized format
      stripePrice = config.displayPrice
      priceNum = config.price // Already a number
    } else if (typeof config.price === 'string') {
      // Old format fallback
      stripePrice = config.price
      priceNum = parseFloat(stripePrice.replace(/[^0-9.]/g, ''))
    } else {
      // Config has numeric price but no displayPrice (shouldn't happen after migration)
      priceNum = config.price
      stripePrice = isAnnual ? `$${priceNum}/year` : `$${priceNum}/month`
    }
  } else {
    // No config found, use hardcoded defaults
    stripePrice = isTeams ?
      (isAnnual ? '$99.99/year' : '$9.99/month') :
      (isAnnual ? '$49.99/year' : '$4.99/month')
    priceNum = parseFloat(stripePrice.replace(/[^0-9.]/g, ''))
  }

  return {
    price: stripePrice,
    monthlyPrice: isAnnual ? `$${(priceNum / 12).toFixed(2)}` : stripePrice.split('/')[0],
    billedText: isAnnual ? `Billed annually ${stripePrice}` : null,
    rawPrice: priceNum
  }
})

export { default as AddCard } from './AddCard'
export { default as AllPayments } from './AllPayments'
export { default as DisplayCard } from './DisplayCard'
export { default as DeleteCard } from './DeleteCard'
export { default as ManageSubscriptions } from './ManageSubscriptions'
export { default as DisplaySubscription } from './DisplaySubscription'
export { default as AddSubscription } from './AddSubscription'
export { default as EnhancedCamModal } from './EnhancedCamModal'
export { default as EnhancedCamModal2 } from './EnhancedCamModal2'
export { default as CellPlanModal } from './CellPlanModal'
export { default as PlusSubscriptionModal } from './PlusSubscriptionModal'
export { default as GiftCodeModal } from './GiftCodeModal'
export { default as SubscribeProcess } from './SubscribeProcess'
export { billingDeferSubscription, billingUpdateUserEmail } from './billingAccess'
export { default as AdminUserSubscriptions } from './AdminUserSubscriptions';
export { default as GiftCodeForm } from './GiftCodeForm';
export { default as SmsAutoRefillToggle } from './SmsAutoRefillToggle';
export { default as SmsMeteredInfo } from './SmsMeteredInfo';
export { default as PlusPlansFeatures } from './PlusPlansFeatures';
export { default as CameraFeaturesTable } from './CameraFeaturesTable';
export { default as HeatStressPlansFeatures } from './HeatStressPlansFeatures';
export { default as PlusSubscriptionSelector } from './PlusSubscriptionSelector';
export { default as CameraSubscriptionSelector } from './CameraSubscriptionSelector';
export { default as HeatStressSubscriptionSelector } from './HeatStressSubscriptionSelector';
export { default as PaymentMethodSelector } from './PaymentMethodSelector';
export { default as SubscriptionsPage } from './SubscriptionsPage';
export { default as SubscriptionTabs } from './SubscriptionTabs';
export { default as PaymentDetailsPage } from './PaymentDetailsPage';
export { default as StepIndicator } from './StepIndicator';
export { default as SubscribePage } from './SubscribePage';
export { default as CheckoutPage } from './CheckoutPage';
export { default as FeatureHub } from './FeatureHub';
export { default as SectionActions } from './SectionActions';
export { default as SubscriptionSuccessMessage } from './SubscriptionSuccessMessage';
export { usePaymentPolling } from './usePaymentPolling';
export { default as UpgradeDowngradeLink, getUpgradeDowngradeAction } from './UpgradeDowngradeLink';
